import { useRef, useCallback } from "react";
import { svgDefs } from "@cadenza/icons";
import { useDrop } from "../../RADHandler/contexts/DropContext.jsx";

/**
 * Hook that manages drag-and-drop functionality for RAD shapes
 * @returns {Object} Object containing drag handlers and utilities
 */
export const useDragGhost = () => {
  const { setDraggedShape } = useDrop();
  const ghostRef = useRef(null);
  const dragStartPoint = useRef({ x: null, y: null });

  const showOverlay = useCallback(() => {
    const overlay = document.getElementById("rad-overlay");
    if (overlay) {
      overlay.style.display = "block";
    }
  }, []);

  const hideOverlay = useCallback(() => {
    const overlay = document.getElementById("rad-overlay");
    if (overlay) {
      overlay.style.display = "none";
    }
  }, []);

  const getSvgKey = useCallback((shapeType) => {
    const typeMap = {
      case: "case_refinement",
      part: "part_refinement",
      repeat: "part_repeat",
      ellipsis: "spring",
      "start-role": "start_role",
    };
    return typeMap[shapeType] || shapeType;
  }, []);

  const createGhostElement = useCallback(
    (shape, pageX, pageY) => {
      const ghostEl = document.createElement("div");
      ghostEl.id = "ghost";
      ghostEl.className = `${shape.class} ${shape.type}`;
      ghostEl.style.position = "absolute";
      ghostEl.style.left = `${pageX}px`;
      ghostEl.style.top = `${pageY}px`;
      ghostEl.style.width = `${shape.width}px`;
      ghostEl.style.height = `${shape.height}px`;
      ghostEl.style.pointerEvents = "none";
      ghostEl.style.opacity = "0.8";
      ghostEl.style.zIndex = "1000";
      ghostEl.style.display = "flex";
      ghostEl.style.alignItems = "center";
      ghostEl.style.justifyContent = "center";

      const iconContainer = document.createElement("div");
      ghostEl.appendChild(iconContainer);

      const svgKey = getSvgKey(shape.type);
      const svgDef = svgDefs[svgKey](shape.width, shape.height);
      const iconHTML = `<svg width="${shape.width}" height="${shape.height}" viewBox="${svgDef.viewBox}" class="drag-ghost-icon">
        ${svgDef.content}
      </svg>`;
      iconContainer.innerHTML = iconHTML;

      document.body.appendChild(ghostEl);
      return ghostEl;
    },
    [getSvgKey]
  );

  const handleMouseDown = useCallback(
    (shape) => (e) => {
      dragStartPoint.current = {
        x: e.pageX,
        y: e.pageY,
      };

      const handleMouseMove = (e) => {
        const distance = Math.sqrt(
          Math.pow(e.pageX - dragStartPoint.current.x, 2) +
            Math.pow(e.pageY - dragStartPoint.current.y, 2)
        );

        // Start drag only after moving some distance
        if (distance > 5 && !ghostRef.current) {
          showOverlay();
          document.body.style.cursor = "grabbing";
          ghostRef.current = createGhostElement(shape, e.pageX, e.pageY);
        }

        if (ghostRef.current) {
          ghostRef.current.style.left = `${e.pageX}px`;
          ghostRef.current.style.top = `${e.pageY}px`;
        }
      };

      const handleMouseUp = (e) => {
        const distance = Math.sqrt(
          Math.pow(e.pageX - dragStartPoint.current.x, 2) +
            Math.pow(e.pageY - dragStartPoint.current.y, 2)
        );

        if (ghostRef.current && distance > 30) {
          // Create the shape data for dropping
          setDraggedShape({
            ...shape,
            x: e.pageX,
            y: e.pageY,
          });
        }

        // Cleanup
        document.body.style.cursor = "default";
        if (ghostRef.current) {
          document.body.removeChild(ghostRef.current);
          ghostRef.current = null;
        }
        hideOverlay();

        // Remove listeners
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [setDraggedShape, showOverlay, hideOverlay, createGhostElement]
  );

  const handleTouchStart = useCallback(
    (shape) => (e) => {
      const touch = e.touches[0];
      if (!touch) {
        return;
      }

      e.preventDefault();

      dragStartPoint.current = {
        x: touch.pageX,
        y: touch.pageY,
      };

      const handleTouchMove = (e) => {
        const moveTouch = e.touches[0];
        if (!moveTouch) {
          return;
        }

        e.preventDefault();

        const distance = Math.sqrt(
          Math.pow(moveTouch.pageX - dragStartPoint.current.x, 2) +
            Math.pow(moveTouch.pageY - dragStartPoint.current.y, 2)
        );

        // Start drag only after moving some distance
        if (distance > 5 && !ghostRef.current) {
          showOverlay();
          document.body.style.cursor = "grabbing";
          ghostRef.current = createGhostElement(
            shape,
            moveTouch.pageX,
            moveTouch.pageY
          );
        }

        if (ghostRef.current) {
          ghostRef.current.style.left = `${moveTouch.pageX}px`;
          ghostRef.current.style.top = `${moveTouch.pageY}px`;
        }
      };

      const handleTouchEnd = (e) => {
        const endTouch = e.changedTouches[0];
        if (!endTouch) {
          return;
        }

        const distance = Math.sqrt(
          Math.pow(endTouch.pageX - dragStartPoint.current.x, 2) +
            Math.pow(endTouch.pageY - dragStartPoint.current.y, 2)
        );

        if (ghostRef.current && distance > 30) {
          // Create the shape data for dropping
          setDraggedShape({
            ...shape,
            x: endTouch.pageX,
            y: endTouch.pageY,
          });
        }

        // Cleanup
        document.body.style.cursor = "default";
        if (ghostRef.current) {
          document.body.removeChild(ghostRef.current);
          ghostRef.current = null;
        }
        hideOverlay();

        // Remove listeners
        document.removeEventListener("touchmove", handleTouchMove);
        document.removeEventListener("touchend", handleTouchEnd);
        document.removeEventListener("touchcancel", handleTouchEnd);
      };

      document.addEventListener("touchmove", handleTouchMove, {
        passive: false,
      });
      document.addEventListener("touchend", handleTouchEnd);
      document.addEventListener("touchcancel", handleTouchEnd);
    },
    [setDraggedShape, showOverlay, hideOverlay, createGhostElement]
  );

  return {
    handleMouseDown,
    handleTouchStart,
  };
};
